import type { ClientMsg, ServerMsg } from "./messages.js";

/**
 * The single swap point for the wire format.
 *
 * Today: UTF-8 JSON over a text WebSocket frame. When we move to a binary
 * format only this file changes — everything else speaks in typed messages.
 */

export type AnyMsg = ClientMsg | ServerMsg;

/** Serializes a message for transmission. */
export function encodeMsg(msg: AnyMsg): string {
  return JSON.stringify(msg);
}

/**
 * Parses an incoming frame. Returns `unknown` on purpose — the result MUST be
 * fed through `validateClientMsg` (server side) or a narrowing check (client
 * side) before use. Returns `null` for anything unparseable.
 */
export function decodeMsg(data: string | ArrayBuffer | Uint8Array): unknown {
  let text: string;
  if (typeof data === "string") {
    text = data;
  } else {
    const bytes =
      data instanceof Uint8Array ? data : new Uint8Array(data as ArrayBuffer);
    text = utf8Decode(bytes);
  }
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

/**
 * Minimal UTF-8 decoder. Uses the platform `TextDecoder` when present (browser,
 * Node >= 11) and otherwise falls back to a hand-rolled loop, so `shared/` has
 * no hard dependency on any host API.
 */
function utf8Decode(bytes: Uint8Array): string {
  const TD = (globalThis as { TextDecoder?: new () => { decode(b: Uint8Array): string } })
    .TextDecoder;
  if (TD) return new TD().decode(bytes);

  let out = "";
  for (let i = 0; i < bytes.length; ) {
    const b0 = bytes[i++] as number;
    if (b0 < 0x80) {
      out += String.fromCharCode(b0);
    } else if (b0 < 0xe0) {
      const b1 = (bytes[i++] as number) & 0x3f;
      out += String.fromCharCode(((b0 & 0x1f) << 6) | b1);
    } else if (b0 < 0xf0) {
      const b1 = (bytes[i++] as number) & 0x3f;
      const b2 = (bytes[i++] as number) & 0x3f;
      out += String.fromCharCode(((b0 & 0x0f) << 12) | (b1 << 6) | b2);
    } else {
      const b1 = (bytes[i++] as number) & 0x3f;
      const b2 = (bytes[i++] as number) & 0x3f;
      const b3 = (bytes[i++] as number) & 0x3f;
      const cp = (((b0 & 0x07) << 18) | (b1 << 12) | (b2 << 6) | b3) - 0x10000;
      out += String.fromCharCode(0xd800 + (cp >> 10), 0xdc00 + (cp & 0x3ff));
    }
  }
  return out;
}
